import * as React from "react";
import { useCallback, useRef, useState } from "react";
import type { Placement } from "@floating-ui/dom";
import { List, Text } from "@bloomberg/media.atomics";
import { Skeleton } from "@bloomberg/intercom";
import { classes } from "@bloomberg/media-ui-core";
import { SubscriberOnlyBenefitOverlay } from "@bloomberg/media-ui-subscriptions";
import { useRegUIClient } from "@foundation/reg-ui-client";

import { trackClick, trackItemClick } from "../tracking";
import type { TopicListItemProps, SingleTopicStoriesProps } from "../types";
import { ALLOWED_TOPICS } from "../constants";
import styles from "./singleTopicStories.module.css";
import { getHeadline } from "~utils/getSanitized";
import LazyRetinaImage from "~components/LazyRetinaImage";

const OPINION_EYEBROWS = ["opinion", "view", "gadfly", "bview"];

export const isOpinion = (brand?: string, franchise?: string) => {
  if (franchise && OPINION_EYEBROWS.includes(franchise)) {
    return true;
  }

  if (brand && OPINION_EYEBROWS.includes(brand)) return true;

  return false;
};

const MODAL_WIDTH = 250;

export const SingleTopicListItem = ({
  story,
  index,
  topicName,
}: TopicListItemProps) => {
  const { primarySite, franchise } = story;
  const isOpinionStory = isOpinion(primarySite, franchise);
  const image = {
    alt: story.image?.description,
    baseUrl: story.image?.baseUrl,
  };
  const text = getHeadline({ headlines: story.headlines });

  const onListItemClick = () => {
    // TODO: replace with updated tracking
    trackItemClick("topicModule", `[${topicName}] ${text}`, index);
  };

  return (
    <article className={styles.listItem}>
      <a
        href={story.url}
        title={text}
        className={classes([styles.listItemContainer, styles.right])}
        onClick={onListItemClick}
      >
        <div
          className={classes([
            styles.mainColumn,
            isOpinionStory ? styles.eyeBrowItem : styles.noEyeBrowItem,
          ])}
        >
          {isOpinionStory && (
            <div className={styles.opinionEyebrow}>Opinion</div>
          )}
          <Text
            color="gray1000"
            fontSize={16}
            fontWeight="medium"
            lineHeight={120}
          >
            {text}
          </Text>
        </div>
        <div className={styles.rightColumn}>
          {story.image && (
            <LazyRetinaImage
              image={image}
              aspectRatio={3 / 2}
              desktopMaxWidth={150}
              mobileMaxWidth={150}
              isLazy={false}
              className={styles.topicItemImage}
            />
          )}
        </div>
      </a>
    </article>
  );
};

const FollowButton = React.forwardRef<
  HTMLButtonElement,
  { onClick: () => void }
>((props, ref) => {
  const { onClick } = props;

  return (
    <button className={styles.followContainer} onClick={onClick} ref={ref}>
      <p className={styles.text}>Follow</p>
    </button>
  );
});

FollowButton.displayName = "FollowButton";

export const SingleTopicStories = ({
  stories,
  topic,
  numberOfRows,
  rootRef,
  isSignedIn,
  followTopicCallback,
}: SingleTopicStoriesProps) => {
  const { client } = useRegUIClient();
  const followButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isSubscriberBenefitOpen, setIsSubscriberBenefitOpen] = useState(false);
  const [modalPosition, setModalPosition] = useState(
    "bottom-start" as Placement
  );

  const handleOnClick = useCallback(() => {
    if (!isSignedIn) {
      trackClick("open", "subscribe_tout");
      setIsSubscriberBenefitOpen(true);
      if (!followButtonRef.current || !rootRef?.current) return;

      const followButtonRect = followButtonRef.current.getBoundingClientRect();
      const rootContainerRect = rootRef?.current.getBoundingClientRect();
      const isCutOff =
        followButtonRect.right > rootContainerRect.right - MODAL_WIDTH;

      setModalPosition(isCutOff ? "bottom-end" : "bottom-start");

      return;
    }

    trackClick("follow", `${topic.id}_storypanel`);
    followTopicCallback(topic.id, true);
  }, [followTopicCallback, topic.id, isSignedIn]);

  const onBenefitExit = useCallback(() => {
    trackClick("close", "subscribe_tout");
    setIsSubscriberBenefitOpen(false);
  }, []);

  return (
    <div className={styles.singleTopicStories}>
      <div className={styles.singleTopicPanelHeader}>
        <div className={styles.singleTopicName}>
          {ALLOWED_TOPICS[topic.id].name}
        </div>
        {!topic.isFollowedByUser && (
          <FollowButton onClick={handleOnClick} ref={followButtonRef} />
        )}
      </div>
      <div className={styles.stories}>
        <List>
          {stories
            ?.filter(Boolean)
            .slice(0, numberOfRows)
            .map((story, index) => (
              <List.Item key={story.id || index}>
                <SingleTopicListItem
                  story={story}
                  index={index}
                  topicName={topic.name}
                />
              </List.Item>
            ))}
        </List>
      </div>
      {isSubscriberBenefitOpen && client && (
        <SubscriberOnlyBenefitOverlay
          anchorElementRef={followButtonRef}
          client={client}
          inSource="homepage-topic-module"
          onExit={onBenefitExit}
          placement={modalPosition}
        >
          Sign in to follow topics and surface the content most relevant to you
        </SubscriberOnlyBenefitOverlay>
      )}
    </div>
  );
};

export const SingleTopicListItemLoading = () => {
  return (
    <div className={styles.listItem}>
      <article className={classes([styles.listItemContainer, styles.right])}>
        <div className={styles.mainColumn}>
          <div className={styles.skeletonTextWrapper}>
            <div className={styles.skeletonLine} data-skeleton-selector />
            <div className={styles.skeletonLine} data-skeleton-selector />
            <div className={styles.skeletonLine} data-skeleton-selector />
          </div>
        </div>
        <div className={styles.rightColumn} data-skeleton-selector>
          <div className={styles.skeletonImage} />
        </div>
      </article>
    </div>
  );
};

React.memo(SingleTopicListItemLoading);

export const SingleTopicStoriesLoading = ({
  numberOfRows,
  length,
}: {
  numberOfRows: number;
  length: number;
}) => {
  return (
    <Skeleton.Skeleton isLoading>
      <div className={styles.topicsStories}>
        {Array.from({ length }).map((_a, indexA) => (
          <div
            className={styles.singleTopicStories}
            key={`topic-skeleton-${indexA}` /** NOSONAR */}
          >
            <div className={styles.skeletonLine} data-skeleton-selector />
            <div className={styles.stories}>
              <List>
                {Array.from({ length: numberOfRows }).map((_b, indexB) => (
                  <List.Item key={`story-skeleton-${indexB}` /** NOSONAR */}>
                    <SingleTopicListItemLoading />
                  </List.Item>
                ))}
              </List>
            </div>
          </div>
        ))}
      </div>
    </Skeleton.Skeleton>
  );
};
